// src/lib/globalTransactionRecovery.ts
// グローバルトランザクションリカバリシステム
// モバイルMetaMaskでトランザクション承認時にタブがサスペンドされた場合の自動リカバリ

import {
  getAllPendingTxByType,
  verifyPendingTx,
  clearPendingTx,
  type PendingTxData,
  type PendingTxType,
} from './pendingTransaction';

/**
 * グローバルリカバリコールバック
 * 各機能（free_mint, stamp_reward等）がこれに登録して、リカバリ時の処理を実装
 */
type RecoveryCallback = (pendingTx: PendingTxData) => Promise<void>;

interface RecoveryCallbacks {
  [key: string]: RecoveryCallback;
}

const recoveryCallbacks: RecoveryCallbacks = {};

/**
 * リカバリコールバックを登録
 *
 * @param type - トランザクション種別
 * @param callback - リカバリ時に実行するコールバック
 */
export function registerRecoveryCallback(type: PendingTxType, callback: RecoveryCallback): void {
  recoveryCallbacks[type] = callback;
}

/**
 * グローバルリカバリハンドラー
 * すべてのペンディングトランザクションをチェックして、完了していればリカバリを実行
 */
async function handleGlobalRecovery(): Promise<void> {
  const types: PendingTxType[] = ['free_mint', 'transfer', 'mint_modal', 'stamp_reward', 'tip'];

  for (const type of types) {
    const pendingList = getAllPendingTxByType(type);

    if (pendingList.length === 0) continue;

    for (const pendingTx of pendingList) {
      try {
        // トランザクションを検証
        const result = await verifyPendingTx(pendingTx, 30_000); // 30秒タイムアウト

        if (result.success && result.receipt) {
          const callback = recoveryCallbacks[type];
          if (callback) {
            await callback(pendingTx);
          }

          // ペンディングをクリア
          clearPendingTx(type, getIdentifierFromPendingTx(pendingTx));
        } else if (result.error && !result.error.includes('timeout')) {
          // エラー（タイムアウト以外） → クリア
          console.error(`[GlobalRecovery] ${type} トランザクション失敗: ${result.error}`);
          clearPendingTx(type, getIdentifierFromPendingTx(pendingTx));
        }
        // タイムアウトの場合は次回再試行のため残す
      } catch (err) {
        console.error(`[GlobalRecovery] ${type} リカバリエラー:`, err);
      }
    }
  }
}

/**
 * PendingTxDataから識別子を生成
 * これは、savePendingTx時に使用されたidentifierを再構築する
 */
function getIdentifierFromPendingTx(pendingTx: PendingTxData): string {
  const { type, metadata, userAddress } = pendingTx;

  switch (type) {
    case 'stamp_reward': {
      const { contractAddress, category, contractType } = metadata;
      if (contractType === 'erc1155') {
        return `stamp_reward_erc1155_${contractAddress}_${category}_${userAddress}`;
      }
      return `stamp_reward_${contractAddress}_${category}_${userAddress}`;
    }
    case 'free_mint': {
      const { contractAddress, tenantId } = metadata;
      return `free_mint_${tenantId || 'global'}_${contractAddress}_${userAddress}`;
    }
    case 'mint_modal': {
      const { contractAddress, category } = metadata;
      return `mint_modal_${contractAddress}_${category}_${userAddress}`;
    }
    case 'transfer':
    case 'tip': {
      // transfer/tipの場合は、metadata に identifier が含まれていることを期待
      return metadata.identifier || `${type}_${userAddress}`;
    }
    default:
      return `${type}_${userAddress}`;
  }
}

let isInitialized = false;

/**
 * グローバルリカバリシステムを初期化
 * アプリ起動時に1回だけ呼び出す
 */
export function initializeGlobalTransactionRecovery(): void {
  if (isInitialized) {
    return;
  }

  // visibilitychangeイベントリスナーを追加
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // タブが復帰したら、少し待ってからリカバリを実行
      setTimeout(() => {
        handleGlobalRecovery().catch((err) => {
          console.error('[GlobalRecovery] Recovery failed:', err);
        });
      }, 500);
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // focusイベントでもチェック
  window.addEventListener('focus', () => {
    setTimeout(() => {
      handleGlobalRecovery().catch((err) => {
        console.error('[GlobalRecovery] Recovery failed:', err);
      });
    }, 500);
  });

  // 定期的なチェック（30秒ごと）- タブがアクティブな場合のみ
  setInterval(() => {
    if (document.visibilityState === 'visible') {
      handleGlobalRecovery().catch((err) => {
        console.error('[GlobalRecovery] Periodic recovery failed:', err);
      });
    }
  }, 30_000);

  isInitialized = true;

  // 初期チェック（アプリ起動時に未完了のトランザクションがあればリカバリ）
  setTimeout(() => {
    handleGlobalRecovery().catch((err) => {
      console.error('[GlobalRecovery] Initial recovery failed:', err);
    });
  }, 1000);
}

/**
 * グローバルリカバリシステムが初期化済みかチェック
 */
export function isGlobalRecoveryInitialized(): boolean {
  return isInitialized;
}
